import { BitmapText, Container } from 'pixi.js'

import data from '/resource/spine/spine_winningPanel_2/spine_winningPanel.skel?url'
import atlas from '/resource/spine/spine_winningPanel_2/spine_winningPanel.atlas?url'

import { Spine } from '@esotericsoftware/spine-pixi'

import { soundManager } from '@slot-nx/core'
import { numberAnimation } from '../../../components/util/numberAnimationUtil'
import { useGameStore } from '../../game/store/GameStore'
import { gameConfig } from '../../../config'
interface IAnimation {
  task: () => void
  duration: number
}

function isMegaWin(odds: number): boolean {
  return odds > 50 && odds <= 100
}

function isSuperWin(odds: number): boolean {
  return odds > 100
}

export const getWinningPanelSpinData = () => {
  const WinningPanelSpineData = [
    { alias: 'winningPanelData', src: data },
    { alias: 'winningPanelAtlas', src: atlas },
  ]
  return WinningPanelSpineData
}

class WinningPanelService {
  public winningPanel: Container
  public winningSpine: Spine

  public async showWinningPanel(odds: number, payout: number) {
    soundManager.stopBGM()
    this.winningPanel.visible = true
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode

    const winSpineContainer = this.winningPanel.getChildByName('spineWinContainer') as Container

    const panelAnimations = this.getWinningPanelAnimations(odds, winSpineContainer)

    const animationTime = panelAnimations.reduce((acc, cur) => acc + cur.duration, 0) / 1000

    const [panelTask, skipPanelTask] = this.playWinBgAnimations(panelAnimations)
    const winAmountContainer = this.winningPanel.getChildByName('winAmount') as Container
    const winAmountText = new BitmapText('0', { fontName: 'Font_TBW', fontSize: 60 })
    winAmountContainer.addChild(winAmountText)
    winAmountText.anchor.set(0.5)

    const [numTask, skipNumTask] = numberAnimation(0, payout, animationTime, winAmountText)

    const skipWinningAnim = () => {
      skipNumTask()
      skipPanelTask()
      soundManager.stop('big_win')
      soundManager.stop('mega_win')
      soundManager.stop('super_mega_win')
      soundManager.play('winning_end')
    }
    const clickCb = () => {
      skipWinningAnim()
      this.winningPanel.onpointertap = null
    }
    this.winningPanel.onpointertap = clickCb

    if (isThisSpinInSpeedMode) {
      skipPanelTask()
      skipNumTask()
      soundManager.play('winning_end')
    }
    await Promise.all([panelTask, numTask])

    await new Promise<void>((resolve) => {
      const closeWinningPanel = () => {
        this.winningPanel.visible = false
        this.winningPanel.onpointertap = null
        winSpineContainer.removeChildren()
        winAmountContainer.removeChildren()
        this.winningSpine.destroy()
        clearTimeout(closeTimeout)
        return resolve()
      }
      this.winningPanel.onpointertap = closeWinningPanel

      const closeTimeout = setTimeout(() => {
        closeWinningPanel()
        return resolve()
      }, gameConfig.autoCloseWinningPanelDurationMs)
    })
  }

  private playWinBgAnimations(animations: IAnimation[]): [Promise<void>, () => void] {
    let isSkip = false
    let anim
    let stopTime = new Date().getTime()

    const task = new Promise<void>((resolve) => {
      const takeNextAnim = () => {
        window.requestAnimationFrame(async () => {
          if (isSkip) {
            anim = animations.pop()
            if (anim) {
              anim.task()
            }
            resolve()
            return
          }
          const now = new Date().getTime()
          if (now >= stopTime) {
            anim = animations.shift()
            if (!anim) {
              resolve()
              return
            }
            anim.task()
            stopTime = now + anim.duration
          }
          takeNextAnim()
        })
      }
      takeNextAnim()
    })

    const skip = () => {
      isSkip = true
    }

    return [task, skip]
  }

  private getWinningPanelAnimations(odds: number, winSpineContainer: Container) {
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode
    this.winningSpine = Spine.from('winningPanelData', 'winningPanelAtlas', { autoUpdate: true })
    winSpineContainer.addChild(this.winningSpine)
    if (isSuperWin(odds)) {
      return [
        {
          duration: 3000,
          task: () => {
            const track = this.winningSpine.state.setAnimation(0, 'big_shine', false)
            if (!isThisSpinInSpeedMode) {
              soundManager.play('super_mega_win')
            }
            setTimeout(() => {
              this.winningSpine.state.setAnimation(0, 'big_loop', false)
            }, (track.animation?.duration as number) * 1000)
          },
        },
        {
          duration: 5000,
          task: () => {
            const track = this.winningSpine.state.setAnimation(0, 'mega_shine', false)
            setTimeout(() => {
              this.winningSpine.state.setAnimation(0, 'mega_loop', true)
            }, (track.animation?.duration as number) * 1000)
          },
        },
        {
          duration: 8000,
          task: () => {
            const track = this.winningSpine.state.setAnimation(0, 'super_shine', false)
            setTimeout(() => {
              this.winningSpine.state.setAnimation(0, 'super_loop', true)
            }, (track.animation?.duration as number) * 1000)
          },
        },
      ]
    }
    if (isMegaWin(odds)) {
      return [
        {
          duration: 3000,
          task: () => {
            const track = this.winningSpine.state.setAnimation(0, 'big_shine', false)
            if (!isThisSpinInSpeedMode) {
              soundManager.play('mega_win')
            }
            setTimeout(() => {
              this.winningSpine.state.setAnimation(0, 'big_loop', true)
            }, (track.animation?.duration as number) * 1000)
          },
        },
        {
          duration: 5000,
          task: () => {
            const track = this.winningSpine.state.setAnimation(0, 'mega_shine', false)
            setTimeout(() => {
              this.winningSpine.state.setAnimation(0, 'mega_loop', true)
            }, (track.animation?.duration as number) * 1000)
          },
        },
      ]
    }
    return [
      {
        duration: 5000,
        task: () => {
          const track = this.winningSpine.state.setAnimation(0, 'big_shine', false)
          if (!isThisSpinInSpeedMode) {
            soundManager.play('big_win')
          }
          setTimeout(() => {
            this.winningSpine.state.setAnimation(0, 'big_loop', true)
          }, (track.animation?.duration as number) * 1000)
        },
      },
    ]
  }
}

export const winningPanelService = new WinningPanelService()
